import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Box,
  Typography,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Category } from '../../types/Category';
import { Content } from '../../types/Content';
import {
  categoryPickerNodeExpandable,
  findCategoryInTree,
  invertSubcaseSelection,
} from '../../utils/CategoryUtils';
import {
  branchChildrenAreLoaded,
  categoryContentsAreLoaded,
} from '../../utils/categoryLoadState';
import { InvertSelectionButton } from '../category/CategoryContent';
import { useHelpContent } from '../../utils/HelpUtils';
import {
  fetchCategoryForPane,
  fetchCategoryWithChildren,
  mergeBranchFetchIntoTree,
  mergeContentsFetchIntoTree,
} from '../../services/CategoryService';

interface DuplicateCategoryAsDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newName: string, selectedCategories: Category[], selectedSubcases: string[]) => void;
  selectedCategory: Category | null;
  categories: Category[];
  existingDocumentNames: string[];
  documentId: string;
  subcaseGroups?: { name: string; subcases: string[] }[];
  documentSubcases?: string[];
}

interface CategorySelectionState {
  [categoryId: string]: {
    selected: boolean;
    children: string[];
    subcases: string[];
  };
}

const DuplicateCategoryAsDocumentDialog: React.FC<DuplicateCategoryAsDocumentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedCategory,
  categories,
  existingDocumentNames,
  documentId,
  subcaseGroups = [],
  documentSubcases = [],
}) => {
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [categorySelection, setCategorySelection] = useState<CategorySelectionState>({});
  const [validationError, setValidationError] = useState('');
  /** Lazy picker tree for the selected scope (one level at a time). */
  const [scopeCategory, setScopeCategory] = useState<Category | null>(null);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);
  const [treeLoadingIds, setTreeLoadingIds] = useState<Set<string>>(new Set());
  const [confirmLoading, setConfirmLoading] = useState(false);
  
  // Subcase selection state - separate for groups and individual selections
  const [selectedSubcaseGroups, setSelectedSubcaseGroups] = useState<string[]>([]);
  const [selectedIndividualSubcases, setSelectedIndividualSubcases] = useState<string[]>([]);
  const [subcaseSelectionCollapsed, setSubcaseSelectionCollapsed] = useState(false);
  const { getHelpText } = useHelpContent();
  
  // Add state for subcase groups and loading
  const [groups, setGroups] = useState<{ name: string; subcases: string[] }[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [allSubcases, setAllSubcases] = useState<string[]>([]);
  /** Individual subcase checkboxes before first group chip; restored when last group chip off. */
  const individualsBaselineBeforeGroupsRef = useRef<string[] | null>(null);
  const scopeCategoryRef = useRef<Category | null>(null);
  scopeCategoryRef.current = scopeCategory;

  const subcasesFromCategory = (category: Category): string[] => {
    const subcases: string[] = [];
    if (category.contentCategory && category.contents) {
      category.contents.forEach((content: Content) => {
        if (content.subcase && !subcases.includes(content.subcase)) {
          subcases.push(content.subcase);
        }
      });
    }
    return subcases;
  };

  const mergeSubcasesFromCategory = useCallback((category: Category) => {
    const found = subcasesFromCategory(category);
    if (found.length === 0) return;
    setAllSubcases((prev) => {
      const next = new Set(prev);
      found.forEach((s) => next.add(s));
      return Array.from(next).sort();
    });
    setSelectedIndividualSubcases((prev) => {
      const next = [...prev];
      for (const s of found) {
        if (!next.includes(s)) next.push(s);
      }
      return next;
    });
  }, []);

  /** Register a loaded node (and its direct children stubs) in the selection map. */
  const registerLoadedSelection = useCallback(
    (category: Category, selected: boolean, state: CategorySelectionState) => {
      const childrenIds = (category.children ?? []).map((c) => c.id);
      state[category.id] = {
        selected,
        children: childrenIds,
        subcases: subcasesFromCategory(category),
      };
      for (const child of category.children ?? []) {
        if (!state[child.id]) {
          state[child.id] = {
            selected,
            children: [],
            subcases: [],
          };
        }
      }
    },
    [],
  );

  // Get all categories currently present in the lazy picker tree
  const getAllCategories = (category: Category): Category[] => {
    const allCategories: Category[] = [category];
    if (!category.contentCategory) {
      (category.children ?? []).forEach((child) => {
        allCategories.push(...getAllCategories(child));
      });
    }
    return allCategories;
  };

  /**
   * After a full fetch on confirm: include a node when it is explicitly selected, or when it was
   * never shown (unloaded) and its nearest loaded ancestor is selected.
   */
  const collectSelectedFromFullTree = (
    full: Category,
    selection: CategorySelectionState,
  ): Category[] => {
    const out: Category[] = [];
    const walk = (cat: Category, parentSelected: boolean) => {
      const selected = selection[cat.id]?.selected ?? parentSelected;
      if (!selected) return;
      out.push(cat);
      for (const child of cat.children ?? []) {
        walk(child, selected);
      }
    };
    walk(full, selection[full.id]?.selected ?? true);
    return out;
  };

  const ensurePickerNodeLoaded = useCallback(
    async (categoryId: string): Promise<Category | null> => {
      const root = scopeCategoryRef.current;
      if (!root) return null;
      const live = findCategoryInTree([root], categoryId);
      if (!live) return null;

      if (live.contentCategory) {
        if (categoryContentsAreLoaded(live)) return live;
        setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
        try {
          const loaded = await fetchCategoryForPane(categoryId);
          setScopeCategory((prev) => {
            if (!prev) return prev;
            const nextRoot = mergeContentsFetchIntoTree([prev], categoryId, loaded)[0] ?? prev;
            scopeCategoryRef.current = nextRoot;
            return nextRoot;
          });
          setCategorySelection((prev) => {
            const next = { ...prev };
            const selected = next[categoryId]?.selected ?? true;
            registerLoadedSelection(loaded, selected, next);
            return next;
          });
          mergeSubcasesFromCategory(loaded);
          return loaded;
        } finally {
          setTreeLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(categoryId);
            return next;
          });
        }
      }

      if (branchChildrenAreLoaded(live)) return live;
      setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
      try {
        const loaded = await fetchCategoryForPane(categoryId);
        setScopeCategory((prev) => {
          if (!prev) return prev;
          const nextRoot = mergeBranchFetchIntoTree([prev], categoryId, loaded)[0] ?? prev;
          scopeCategoryRef.current = nextRoot;
          return nextRoot;
        });
        setCategorySelection((prev) => {
          const next = { ...prev };
          const selected = next[categoryId]?.selected ?? true;
          registerLoadedSelection(loaded, selected, next);
          return next;
        });
        return loaded;
      } finally {
        setTreeLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(categoryId);
          return next;
        });
      }
    },
    [mergeSubcasesFromCategory, registerLoadedSelection],
  );

  useEffect(() => {
    if (!open || !selectedCategory) {
      setScopeCategory(null);
      scopeCategoryRef.current = null;
      setScopeError(null);
      setScopeLoading(false);
      return;
    }

    let cancelled = false;
    let baseName = selectedCategory.name.trim();
    let counter = 1;
    let nextName = baseName;
    const cleanExistingNames = existingDocumentNames.map((name) => name.trim());
    while (cleanExistingNames.includes(nextName)) {
      nextName = `${baseName} (${counter})`;
      counter++;
    }
    setNewName(nextName);
    setSelectedSubcaseGroups([]);
    individualsBaselineBeforeGroupsRef.current = null;
    setSubcaseSelectionCollapsed(false);
    setGroups(subcaseGroups);
    setCategorySelection({});
    const initialSubcases = [...documentSubcases].sort();
    setAllSubcases(initialSubcases);
    setSelectedIndividualSubcases([...initialSubcases]);
    setScopeError(null);
    setScopeLoading(true);
    setExpandedNodeIds([selectedCategory.id]);
    setTreeLoadingIds(new Set());
    setConfirmLoading(false);

    void (async () => {
      try {
        // One level only — expand loads deeper branches.
        const loaded = await fetchCategoryForPane(selectedCategory.id);
        if (cancelled) return;
        scopeCategoryRef.current = loaded;
        setScopeCategory(loaded);
        const selection: CategorySelectionState = {};
        registerLoadedSelection(loaded, true, selection);
        setCategorySelection(selection);
        mergeSubcasesFromCategory(loaded);
      } catch (err) {
        if (cancelled) return;
        setScopeCategory(null);
        scopeCategoryRef.current = null;
        setScopeError(
          err instanceof Error ? err.message : 'Failed to load category structure for duplication',
        );
      } finally {
        if (!cancelled) {
          setScopeLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedCategory,
    existingDocumentNames,
    open,
    documentId,
    documentSubcases,
    registerLoadedSelection,
    mergeSubcasesFromCategory,
  ]);

  // Sync groups from the prop whenever it updates while the dialog is open.
  // The main initialization effect above runs once on open, but MainLayout fetches
  // subcaseGroups asynchronously, so the prop may arrive after the effect has run.
  useEffect(() => {
    if (open) {
      setGroups(subcaseGroups);
    }
  }, [open, subcaseGroups]);

  useEffect(() => {
    if (open) {
      setValidationError('');
    } else {
      setSelectedIndividualSubcases([]);
      setNameError('');
      setSelectedSubcaseGroups([]);
      setCategorySelection({});
      setValidationError('');
      setScopeCategory(null);
      scopeCategoryRef.current = null;
      setScopeError(null);
      setScopeLoading(false);
      setExpandedNodeIds([]);
      setTreeLoadingIds(new Set());
      setConfirmLoading(false);
      individualsBaselineBeforeGroupsRef.current = null;
    }
  }, [open]);

  // Helper to validate the document name against selected subcategories
  const validateNameError = (name: string, categorySelection: CategorySelectionState) => {
    if (!scopeCategory) {
      setNameError('');
      return;
    }
    const selectedSubcategoryNames = (scopeCategory.children || [])
      .filter(child => categorySelection[child.id]?.selected)
      .map(child => child.name.trim().toLowerCase());
    if (!name.trim()) {
      setNameError('Document name cannot be empty');
    } else if (existingDocumentNames.includes(name.trim())) {
      setNameError('A document with this name already exists');
    } else if (selectedSubcategoryNames.includes(name.trim().toLowerCase())) {
      setNameError('Document name cannot be the same as a selected subcategory of the root category');
    } else {
      setNameError('');
    }
  };

  // Re-validate name error when category selection changes
  useEffect(() => {
    if (!scopeCategory) return;
    validateNameError(newName, categorySelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySelection, scopeCategory]);

  // Guard clause: if no selectedCategory, don't render the dialog
  if (!selectedCategory) {
    return null;
  }

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewName(value);
    validateNameError(value, categorySelection);
  };

  const handleCategoryToggle = (categoryId: string) => {
    if (!scopeCategory) return;
    const scopeRoot = scopeCategory;
    setCategorySelection(prev => {
      const newState = { ...prev };
      const category = newState[categoryId];
      
      if (category) {
        const newSelected = !category.selected;
        category.selected = newSelected;
        
        // If unselecting a parent, unselect all children
        if (!newSelected) {
          const unselectChildren = (parentId: string) => {
            const parent = newState[parentId];
            if (parent && parent.children) {
              parent.children.forEach(childId => {
                if (newState[childId]) {
                  newState[childId].selected = false;
                  unselectChildren(childId);
                }
              });
            }
          };
          unselectChildren(categoryId);
        }
        
        // If selecting a child, ensure all parents up to root are selected
        if (newSelected) {
          const ensureParentsSelected = (childId: string) => {
            // Find the parent of this category in the tree structure
            const findParent = (root: Category, targetId: string): Category | null => {
              if (root.children) {
                for (const child of root.children) {
                  if (child.id === targetId) {
                    return root;
                  }
                  const found = findParent(child, targetId);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const parent = findParent(scopeRoot, childId);
            if (parent && newState[parent.id]) {
              newState[parent.id].selected = true;
              ensureParentsSelected(parent.id);
            }
          };
          ensureParentsSelected(categoryId);
        }
      }
      
      return newState;
    });

    // Validate selection immediately after state update
    setTimeout(() => {
      validateSelectionInRealTime();
    }, 0);
  };

  // Real-time validation function
  const validateSelectionInRealTime = () => {
    if (!scopeCategory) return;
    const scopeRoot = scopeCategory;
    setCategorySelection(prev => {
      const newState = { ...prev };
      let hasValidationError = false;
      let errorMessage = '';

      // Check each selected category
      const selectedCategories = Object.entries(newState)
        .filter(([_, state]) => state.selected)
        .map(([id, _]) => id);
      
      const invalidCategories: string[] = [];
      
      for (const categoryId of selectedCategories) {
        const category = newState[categoryId];
        if (category && category.children.length > 0) {
          const selectedChildren = category.children.filter(childId => 
            newState[childId]?.selected
          );
          if (selectedChildren.length > 0 && selectedChildren.length < 2) {
            const categoryName = getAllCategories(scopeRoot).find(cat => cat.id === categoryId)?.name || 'Unknown';
            invalidCategories.push(categoryName);
          }
        }
      }
      
      if (invalidCategories.length > 0) {
        hasValidationError = true;
        if (invalidCategories.length === 1) {
          errorMessage = `Category "${invalidCategories[0]}" must have at least 2 subcategories selected`;
        } else {
          errorMessage = `Categories must have at least 2 subcategories selected: ${invalidCategories.join(', ')}`;
        }
      }
      
      setValidationError(errorMessage);
      return newState;
    });
  };

  /** Effective selection for confirm/UI; group chips keep this list in sync (same as Duplicate Document). */
  const getSelectedSubcases = (): string[] => selectedIndividualSubcases;

  const subcasesUnionForGroupNames = (names: string[]) => {
    const acc = new Set<string>();
    names.forEach(n => {
      const g = groups.find(x => x.name === n);
      g?.subcases.forEach(s => acc.add(s));
    });
    return acc;
  };

  const allCategorySubcasesSelected = (individuals: string[]) =>
    allSubcases.length > 0 &&
    individuals.length === allSubcases.length &&
    allSubcases.every(s => individuals.includes(s));

  const handleSubcaseGroupChipClick = (group: { name: string; subcases: string[] }) => {
    const g = groups.find(x => x.name === group.name);
    if (!g) return;

    setSelectedSubcaseGroups(prevNames => {
      const turningOff = prevNames.includes(group.name);
      const nextNames = turningOff
        ? prevNames.filter(n => n !== group.name)
        : [...prevNames, group.name];

      setSelectedIndividualSubcases(prevSub => {
        if (!turningOff) {
          if (prevNames.length === 0) {
            individualsBaselineBeforeGroupsRef.current = [...prevSub];
          }
          if (allCategorySubcasesSelected(prevSub) && prevNames.length === 0) {
            return [...g.subcases];
          }
          const merged = [...prevSub];
          for (const s of g.subcases) {
            if (merged.indexOf(s) === -1) merged.push(s);
          }
          return merged;
        }
        const cover = subcasesUnionForGroupNames(nextNames);
        const filtered = prevSub.filter(s => !g.subcases.includes(s) || cover.has(s));
        if (nextNames.length === 0) {
          if (individualsBaselineBeforeGroupsRef.current !== null) {
            const restored = [...individualsBaselineBeforeGroupsRef.current];
            individualsBaselineBeforeGroupsRef.current = null;
            return restored;
          }
          return filtered.length === 0 ? [...allSubcases] : filtered;
        }
        return filtered;
      });

      return nextNames;
    });
  };

  // Validate that each selected category has at least 2 children selected
  const validateSelection = (): boolean => {
    if (!scopeCategory) return false;
    const selectedCategories = Object.entries(categorySelection)
      .filter(([_, state]) => state.selected)
      .map(([id, _]) => id);
    
    const invalidCategories: string[] = [];
    
    for (const categoryId of selectedCategories) {
      const category = categorySelection[categoryId];
      if (category && category.children.length > 0) {
        const selectedChildren = category.children.filter(childId => 
          categorySelection[childId]?.selected
        );
        if (selectedChildren.length > 0 && selectedChildren.length < 2) {
          const categoryName = getAllCategories(scopeCategory).find(cat => cat.id === categoryId)?.name || 'Unknown';
          invalidCategories.push(categoryName);
        }
      }
    }
    
    if (invalidCategories.length > 0) {
      if (invalidCategories.length === 1) {
        setValidationError(`Category "${invalidCategories[0]}" must have at least 2 subcategories selected`);
      } else {
        setValidationError(`Categories must have at least 2 subcategories selected: ${invalidCategories.join(', ')}`);
      }
      return false;
    }
    
    setValidationError('');
    return true;
  };

  const handleNodeToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpandedNodeIds(nodeIds);
    const newlyExpanded = nodeIds.filter((id) => !expandedNodeIds.includes(id));
    for (const id of newlyExpanded) {
      const root = scopeCategoryRef.current;
      if (!root) continue;
      const cat = findCategoryInTree([root], id);
      if (cat && !cat.contentCategory && !branchChildrenAreLoaded(cat)) {
        void ensurePickerNodeLoaded(id);
      }
    }
  };

  const handleConfirm = async () => {
    if (!newName.trim() || nameError || !scopeCategory || confirmLoading) {
      return;
    }

    if (!validateSelection()) {
      return;
    }

    setConfirmLoading(true);
    setScopeError(null);
    try {
      // Full scope fetch only on confirm so unexpanded selected branches are still copied.
      const full = await fetchCategoryWithChildren(scopeCategory.id);
      const selectedCategories = collectSelectedFromFullTree(full, categorySelection);
      onConfirm(newName.trim(), selectedCategories, getSelectedSubcases());
      onClose();
    } catch (err) {
      setScopeError(
        err instanceof Error ? err.message : 'Failed to load categories for duplication',
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  // Render category structure
  const renderCategoryTree = (category: Category, level: number = 0): React.ReactNode => {
    const isSelected = categorySelection[category.id]?.selected || false;
    const expandable = categoryPickerNodeExpandable(category);
    const childrenLoaded = branchChildrenAreLoaded(category);
    const hasLoadedChildren = (category.children?.length ?? 0) > 0;
    const loading = treeLoadingIds.has(category.id);

    const label = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Checkbox
          checked={isSelected}
          onChange={() => handleCategoryToggle(category.id)}
          onClick={(e) => e.stopPropagation()}
          size="small"
        />
        {category.contentCategory ? <DescriptionIcon /> : <FolderIcon />}
        <Typography variant="body2">
          {category.name}
          {loading && (
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              (loading…)
            </Typography>
          )}
          {category.contentCategory &&
            categoryContentsAreLoaded(category) &&
            category.contents && (
            <Typography component="span" variant="caption" color="text.secondary">
              {` (${category.contents.length} items)`}
            </Typography>
          )}
        </Typography>
      </Box>
    );

    // Content categories must not pass children to TreeItem — MUI shows a chevron for any
    // child nodes, including `false` from `condition && …` expressions.
    if (category.contentCategory) {
      return (
        <TreeItem
          key={category.id}
          nodeId={category.id}
          label={label}
          sx={{ pl: level * 2 }}
        />
      );
    }

    return (
      <TreeItem
        key={category.id}
        nodeId={category.id}
        label={label}
        sx={{ pl: level * 2 }}
      >
        {hasLoadedChildren
          ? category.children!.map((child) => renderCategoryTree(child, level + 1))
          : null}
        {expandable && !childrenLoaded && !hasLoadedChildren ? (
          <TreeItem
            nodeId={`${category.id}__loading`}
            label={
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            }
            disabled
          />
        ) : null}
      </TreeItem>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Duplicate as document</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Create a new document from the branch under{' '}
          <strong>{selectedCategory?.name ?? 'the selected category'}</strong>. Choose which
          categories to include and which subcases — only matching content rows are copied.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Tooltip title={getHelpText('duplicate-category-document-name')}>
            <TextField
              autoFocus
              margin="dense"
              label="New Document Name"
              placeholder={`${selectedCategory.name} - Document`}
              fullWidth
              value={newName}
              onChange={handleNameChange}
              error={!!nameError}
              helperText={nameError}
            />
          </Tooltip>
        </Box>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">Select Categories to Include</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Categories must have at least 2 subcategories selected
          </Typography>
          <Tooltip title={getHelpText('duplicate-category-category-tree')}>
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
              {scopeLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading categories…</Typography>
                </Box>
              )}
              {scopeError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {scopeError}
                </Alert>
              )}
              {!scopeLoading && scopeCategory && (
                <TreeView
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  defaultExpandIcon={<ChevronRightIcon />}
                  expanded={expandedNodeIds}
                  onNodeToggle={handleNodeToggle}
                >
                  {renderCategoryTree(scopeCategory)}
                </TreeView>
              )}
            </Box>
          </Tooltip>
          {validationError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {validationError}
            </Alert>
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Tooltip title={getHelpText('duplicate-category-select-subcases')}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!subcaseSelectionCollapsed}
                      onChange={(e) => setSubcaseSelectionCollapsed(!e.target.checked)}
                      size="small"
                    />
                  }
                  label="Select Subcases to Include"
                />
              </span>
            </Tooltip>
            <Tooltip title={getHelpText('duplicate-category-subcase-selection-toggle')}>
              <IconButton
                size="small"
                onClick={() => setSubcaseSelectionCollapsed(!subcaseSelectionCollapsed)}
                sx={{ p: 0.5 }}
              >
                {subcaseSelectionCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          
          {!subcaseSelectionCollapsed && (
            <Box sx={{ mt: 1, ml: 3 }}>
              {/* Subcase Groups */}
              {loadingGroups ? (
                <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading groups...</Typography>
                </Box>
              ) : groups.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Subcase Groups:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {groups.map(group => (
                      <Tooltip key={group.name} title={getHelpText('duplicate-category-subcase-groups')}>
                        <Chip
                          label={group.name}
                          size="small"
                          color={selectedSubcaseGroups.includes(group.name) ? 'primary' : 'default'}
                          onClick={() => handleSubcaseGroupChipClick(group)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Individual Subcases */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Individual Subcases:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Tooltip title={getHelpText('duplicate-category-subcase-select-all')}>
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedIndividualSubcases.length === allSubcases.length}
                            indeterminate={
                              selectedIndividualSubcases.length > 0 &&
                              selectedIndividualSubcases.length < allSubcases.length
                            }
                            onChange={e => {
                              individualsBaselineBeforeGroupsRef.current = null;
                              if (e.target.checked) {
                                setSelectedIndividualSubcases([...allSubcases]);
                                setSelectedSubcaseGroups([]);
                              } else {
                                setSelectedIndividualSubcases([]);
                                setSelectedSubcaseGroups([]);
                              }
                            }}
                            size="small"
                          />
                        }
                        label="Select All"
                      />
                    </span>
                  </Tooltip>
                  <InvertSelectionButton
                    disabled={allSubcases.length === 0}
                    onClick={() => {
                      individualsBaselineBeforeGroupsRef.current = null;
                      setSelectedSubcaseGroups([]);
                      setSelectedIndividualSubcases(prev =>
                        invertSubcaseSelection(allSubcases, prev),
                      );
                    }}
                  />
                </Box>
                <Box sx={{ maxHeight: 120, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                  {allSubcases.map((subcase: string) => (
                    <Tooltip key={subcase} title={getHelpText('duplicate-category-subcase-individual-item')}>
                      <span>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedIndividualSubcases.includes(subcase)}
                              onChange={(e) => {
                                setSelectedIndividualSubcases(prev =>
                                  e.target.checked
                                    ? [...prev, subcase]
                                    : prev.filter(s => s !== subcase)
                                );
                              }}
                              size="small"
                            />
                          }
                          label={subcase}
                          sx={{ display: 'block', mb: 0.5 }}
                        />
                      </span>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
          
          {/* Selected subcases summary - always show when filter is active */}
          {!subcaseSelectionCollapsed && (() => {
            const selectedSubcases = getSelectedSubcases();
            if (selectedSubcases.length > 0) {
              return (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 3 }}>
                  Selected: {selectedSubcases.join(', ')}
                </Typography>
              );
            }
            return null;
          })()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Tooltip title={getHelpText('duplicate-category-cancel')}>
          <Button onClick={onClose}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getHelpText('duplicate-category-confirm')}>
          <Button 
            onClick={() => void handleConfirm()} 
            variant="contained"
            disabled={
              scopeLoading ||
              confirmLoading ||
              !scopeCategory ||
              !newName.trim() ||
              !!nameError ||
              !!validationError
            }
          >
            {confirmLoading ? 'Duplicating…' : 'Duplicate as Document'}
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateCategoryAsDocumentDialog; 